import {
  Component,
  computed,
  effect,
  inject,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { MatIcon } from '@angular/material/icon';
import { Router, NavigationEnd } from '@angular/router';
import { I18nPipe } from '../../shared/i18n/i18n-pipe';
import { I18nScopedService } from '../../shared/i18n/i18n-scoped.service';
import { provideI18nScope } from '../../shared/i18n/i18n-scope.provider';
import { DashboardService } from '../../features/dashboard/services/dashboard/dashboard.service';
import { MatDialog } from '@angular/material/dialog';
import { AddDashboardDialog } from '../dashboard/add-dashboard-dialog/add-dashboard-dialog';
import { firstValueFrom, filter, map, tap } from 'rxjs';
import { Dashboard } from '../../features/dashboard/models/dashboard.model';
import { I18nScopeReadyDirective } from '../../shared/i18n/i18n-scope-ready.directive';
import { ProjectService } from '../../shared/services/project/project.service';
import { DashboardListItem } from '../../features/dashboard/models/dashboard-list-item/dashboard-list-item.model';
import { MatTooltip } from '@angular/material/tooltip';
import { HasPermissionDirective } from '../../shared/directives/has-permission.directive';
import { Permissions } from '@anessa/shared-models';
import { TimeSeriesTablesService } from '../../features/timeseries-data/services/timeseries-tables.service';
import { TimeSeriesTablesUiService } from '../timeseries/timeseries-tables-ui.service';
import { SectionConfigApiService } from '../../features/data-management/section-config-api.service';
import {
  SectionManagerDialog,
  type SectionManagerDialogAdapter,
  type SectionManagerDialogConfig,
} from '../../shared/common/section-manager-dialog/section-manager-dialog';
import { ReportService } from '../../features/report-creation/services/report.service';
import { PermissionService } from '../../shared/services/permissions/permission.service';

interface NavItem {
  label: string;
  icon?: string;
  symbolIcon?: string;
  route: string;
  disabled?: boolean;
  viewPermission?: string;
  editPermission?: string;
  /** When true, the item is disabled until a project is selected. Good to have
   * until we have the full project/plant flow figured out so the sidebar can handle it*/
  requiresProject?: boolean;
}

interface NavChild {
  label: string;
  route: string;
  permission?: string;
  isRawLabel?: boolean;
  translateLabel?: boolean;
}

interface NavGroup {
  label: string;
  icon: string;
  children: NavChild[];
}

@Component({
  selector: 'app-sidebar',
  imports: [
    MatIcon,
    I18nPipe,
    I18nScopeReadyDirective,
    MatTooltip,
    HasPermissionDirective,
    NgTemplateOutlet,
    CdkDropList,
    CdkDrag,
  ],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss',
  providers: [provideI18nScope('ui/sidebar'), I18nScopedService],
})
export class Sidebar implements OnInit {
  private readonly router = inject(Router);
  private readonly dashboardService = inject(DashboardService);
  private readonly reportService = inject(ReportService);
  private readonly dialog = inject(MatDialog);
  private readonly timeseriesTablesService = inject(TimeSeriesTablesService);
  private readonly timeseriesTablesUi = inject(TimeSeriesTablesUiService);
  private readonly sectionConfigApi = inject(SectionConfigApiService);
  private readonly permissionService = inject(PermissionService, {
    optional: true,
  });
  readonly projectService = inject(ProjectService);
  readonly DASHBOARD_ROUTES = ['/dashboard', '/overview'];
  readonly REPORT_ROUTE = '/reports';

  constructor() {
    // Listen to route changes to update active state,
    // this is necessary to keep the sidebar in sync when navigating in other components (ex: navigating to first dashboard on login)
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((e) => {
        this.syncActiveStateFromUrl(e.urlAfterRedirects);
        void this.permissionService?.refreshPermissionsForActivePlant({
          force: true,
        });
      });

    effect(() => {
      const selectedDashboardName =
        this.dashboardService.selectedDashboardName();
      const activeRoute = this.activeRoute();
      if (!selectedDashboardName) return;
      if (!this.DASHBOARD_ROUTES.includes(activeRoute)) return;

      this.activeSubroute = selectedDashboardName;
    });
  }
  protected readonly Permissions = Permissions;
  protected readonly showDeactivatedSidebarItems = false;

  private readonly deactivatedPlaceholderNavItems: NavItem[] = [
    {
      label: 'notifications',
      icon: 'notifications',
      route: '/notifications',
      disabled: true,
    },
    { label: 'trends', icon: 'trending_up', route: '/trends', disabled: true },
    {
      label: 'assets',
      icon: 'precision_manufacturing',
      route: '/assets',
      disabled: true,
    },
    {
      label: 'analytics',
      icon: 'analytics',
      route: '/analytics',
      disabled: true,
    },
    {
      label: 'simulation',
      icon: 'model_training',
      route: '/simulation',
      disabled: true,
    },
    {
      label: 'optimization',
      icon: 'tune',
      route: '/optimization',
      disabled: true,
    },
  ];

  // labels are translation keys, to be used with the i18n pipe in the template
  protected readonly navItems: NavItem[] = [
    { label: 'plants', icon: 'factory', route: '/plants' },
    {
      label: 'overview',
      icon: 'home',
      route: '/overview',
      requiresProject: true,
      viewPermission: Permissions.VIEW_SHARED_DASHBOARDS,
      editPermission: Permissions.EDIT_SHARED_DASHBOARDS,
    },
    {
      label: 'dashboards',
      icon: 'dashboard',
      route: '/dashboard',
      requiresProject: true,
      viewPermission: Permissions.VIEW_INDIVIDUAL_DASHBOARDS,
      editPermission: Permissions.EDIT_INDIVIDUAL_DASHBOARDS,
    },
    {
      label: 'insights',
      symbolIcon: 'psychology',
      route: '/insights',
      requiresProject: true,
      viewPermission: Permissions.VIEW_METRICS,
    },
    {
      label: 'reports',
      icon: 'description',
      route: '/reports',
      requiresProject: true,
    },
    {
      label: 'flare_analysis',
      icon: 'local_fire_department',
      route: '/flare-analysis',
      requiresProject: true,
      viewPermission: Permissions.VIEW_FLARE_ANALYSIS,
    },
    ...(this.showDeactivatedSidebarItems
      ? this.deactivatedPlaceholderNavItems
      : []),
  ];

  @Input() collapsed = false;

  protected activeRoute = signal<string>('/overview');
  protected currentUrl = signal<string>('/');
  protected activeSubroute = '';

  readonly dataManagementExpanded = signal(false);
  readonly weighbridgeExpanded = signal(false);
  readonly timeseriesExpanded = signal(false);
  readonly dataManagementRouteActive = signal(false);
  readonly weighbridgeRouteActive = signal(false);
  readonly timeseriesRouteActive = signal(false);

  protected readonly dataManagementGroup: NavGroup = {
    label: 'data_management',
    icon: 'storage',
    children: [],
  };

  readonly dataManagementChildren = signal<NavChild[]>([]);

  protected readonly weighbridgeGroup: NavGroup = {
    label: 'weighbridge',
    icon: 'scale',
    children: [
      {
        label: 'nav_weighbridge_data',
        route: '/data-management/weighbridge/data',
      },
      {
        label: 'nav_weighbridge_mapping',
        route: '/data-management/weighbridge/mapping',
      },
    ],
  };

  protected readonly timeseriesDataGroup = computed<NavGroup>(() => ({
    label: 'timeseries_data',
    icon: 'table_chart',
    children: this.timeseriesTablesService
      .tables()
      .filter((t) => t.visible)
      .sort((a, b) => a.order - b.order)
      .map((table) => ({
        label: table.name,
        route: `/timeseries-data/${table.id}`,
        isRawLabel: true,
      })),
  }));

  ngOnInit() {
    this.syncActiveStateFromUrl(this.router.url);
    void this.permissionService?.refreshPermissionsForActivePlant({
      force: true,
    });
    this.loadSectionChildren();
  }

  private loadSectionChildren(): void {
    this.sectionConfigApi.getAll().subscribe((configs) => {
      const children = configs
        .filter((c) => c.visible)
        .sort((a, b) => a.order - b.order)
        .map((c) => ({
          label: c.label,
          route: `/data-management/${c.key}`,
          isRawLabel: true,
        }));
      this.dataManagementChildren.set(children);
    });
  }

  openSectionManager(): void {
    const adapter: SectionManagerDialogAdapter = {
      loadItems: () =>
        this.sectionConfigApi.getAll().pipe(
          map((configs) =>
            configs.map((c) => ({
              id: c.id,
              key: c.key,
              label: c.label,
              visible: c.visible,
            })),
          ),
        ),
      createItem: (dto) =>
        this.sectionConfigApi.create(dto).pipe(
          map((c) => ({
            id: c.id,
            key: c.key,
            label: c.label,
            visible: c.visible,
          })),
        ),
      updateItem: (id, dto) => this.sectionConfigApi.update(id, dto),
      deleteItem: (item) =>
        this.sectionConfigApi.remove(item.id).pipe(
          tap(() => {
            if (this.router.url.includes(item.key)) {
              void this.router.navigate(['/data-management']);
            }
          }),
        ),
    };

    this.dialog
      .open(SectionManagerDialog, {
        width: '560px',
        data: { adapter } satisfies SectionManagerDialogConfig,
      })
      .afterClosed()
      .subscribe((changed) => {
        if (changed) this.loadSectionChildren();
      });
  }

  /**
   * Return dashboard per overview and dashboard but check which section is active
   * to determine if active, if so return the full list otherwise always return
   * the first 3 dashboards.
   */
  readonly dashboardsPerRoute = computed<Record<string, DashboardListItem[]>>(
    () => {
      const activeRoute = this.activeRoute();
      const allDashboards = this.dashboardService.dashboardList();
      const overviewList = allDashboards.filter((d) => d.isOverview);
      const dashboardList = allDashboards.filter((d) => !d.isOverview);

      return {
        '/overview':
          activeRoute === '/overview' ? overviewList : overviewList.slice(0, 3),
        '/dashboard':
          activeRoute === '/dashboard'
            ? dashboardList
            : dashboardList.slice(0, 3),
      };
    },
  );

  readonly reportsForSidebar = computed(() => {
    const allReports = this.reportService.reportList();
    if (this.activeRoute() === this.REPORT_ROUTE) {
      return allReports;
    }
    return allReports.slice(0, 3);
  });

  /** Sync active sidebar styles from a given URL path */
  syncActiveStateFromUrl(url: string) {
    this.currentUrl.set(url);
    const segments = url.split('/').filter(Boolean);
    if (segments[0]) {
      this.activeRoute.set('/' + segments[0]);
      if (segments[1]) {
        this.activeSubroute = decodeURIComponent(segments[1]);
        if (this.DASHBOARD_ROUTES.includes('/' + segments[0])) {
          this.dashboardService.selectedDashboardName.set(this.activeSubroute);
        }
        if ('/' + segments[0] === this.REPORT_ROUTE) {
          this.reportService.selectedReportId.set(this.activeSubroute);
        }
      } else {
        this.activeSubroute = '';
      }
    } else {
      // Root path renders the project selection (plants) page
      this.activeRoute.set('/plants');
      this.activeSubroute = '';
    }

    if (url.startsWith('/data-management/weighbridge')) {
      this.weighbridgeExpanded.set(true);
      this.weighbridgeRouteActive.set(true);
      this.dataManagementRouteActive.set(false);
      this.timeseriesRouteActive.set(false);
    } else if (url.startsWith('/timeseries-data')) {
      this.timeseriesExpanded.set(true);
      this.timeseriesRouteActive.set(true);
      this.dataManagementRouteActive.set(false);
      this.weighbridgeRouteActive.set(false);
    } else if (url.startsWith('/data-management')) {
      this.dataManagementExpanded.set(true);
      this.dataManagementRouteActive.set(true);
      this.weighbridgeRouteActive.set(false);
      this.timeseriesRouteActive.set(false);
    } else {
      this.dataManagementRouteActive.set(false);
      this.weighbridgeRouteActive.set(false);
      this.timeseriesRouteActive.set(false);
    }
  }

  navigateToChild(child: NavChild): void {
    this.router.navigate([child.route]).then(() => null);
  }

  isChildActive(child: NavChild): boolean {
    //decode URI as keys with spaces will show up as 'key%20space' where as child route would show 'key space'
    //look for exact string matching instead of startsWith, as startsWith would result in true if a key included another key (example, key and key2)
    return decodeURIComponent(this.currentUrl()) === child.route;
  }

  protected isNavItemDisabled(item: NavItem): boolean {
    if (item.disabled) return true;
    return !!(item.requiresProject && !this.projectService.currentProject());
  }

  navigateTo(item: NavItem, subpath?: string) {
    if (this.isNavItemDisabled(item)) return;
    const route = [item.route];
    if (subpath !== null && subpath !== undefined) {
      route.push(subpath);
    } else {
      // get first dashboard and show that if found instead of default page
      const firstDashboard = this.dashboardsPerRoute()[item.route]?.[0];
      if (firstDashboard) {
        route.push(firstDashboard.name);
      }

      if (item.route === this.REPORT_ROUTE) {
        const firstReport = this.reportsForSidebar()[0];
        if (firstReport) {
          route.push(firstReport.id);
        }
      }
    }
    this.router.navigate(route).then(() => null);
  }

  get isOverview() {
    return this.activeRoute() === '/overview';
  }

  onDashboardDrop(event: CdkDragDrop<DashboardListItem[]>, route: string) {
    if (
      route !== this.activeRoute() ||
      event.previousIndex === event.currentIndex
    ) {
      return;
    }

    const isOverviewRoute = route === '/overview';
    const allDashboards = this.dashboardService.dashboardList();
    const routeDashboards = allDashboards.filter(
      (d) => d.isOverview === isOverviewRoute,
    );

    moveItemInArray(routeDashboards, event.previousIndex, event.currentIndex);

    let routeDashboardIndex = 0;
    const reorderedDashboardList = allDashboards.map((dashboard) =>
      dashboard.isOverview === isOverviewRoute
        ? routeDashboards[routeDashboardIndex++]
        : dashboard,
    );

    this.dashboardService.dashboardList.set(reorderedDashboardList);
    this.dashboardService.saveDashboardOrderForCurrentProject();
  }

  addDashboard() {
    const currentProject = this.projectService.currentProject();
    if (!currentProject) throw new Error('no project selected');

    const routePrefix = this.router.url.includes('/overview')
      ? '/overview'
      : '/dashboard';

    this.dialog
      .open(AddDashboardDialog, {
        data: { isOverview: this.isOverview, currentProject: currentProject },
      })
      .afterClosed()
      .subscribe(async (dashboard?: Dashboard) => {
        if (!dashboard?.name) return;
        this.saveAndRedirect(dashboard, routePrefix).then(() => null);
      });
  }

  addTimeSeriesTable(): void {
    this.timeseriesTablesUi.openAddTableDialog();
  }

  openTimeseriesSectionManager(): void {
    this.timeseriesTablesUi.openSectionManagerDialog();
  }

  async saveAndRedirect(dashboard: Dashboard, routePrefix: string) {
    try {
      await firstValueFrom(this.dashboardService.saveLayout(dashboard));
      this.dashboardService.selectedDashboardName.set(dashboard.name);
      this.activeSubroute = dashboard.name;
      await this.router.navigate([routePrefix, `${dashboard.name}`]);
    } catch (err) {
      console.error('Failed to save dashboard', err);
    }
  }

  addReport(): void {
    const currentProject = this.projectService.currentProject();
    if (!currentProject) return;

    this.reportService.openCreateReportDialog(currentProject);
  }
}
